import React, { useState } from 'react';
import { Switch } from 'antd';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import { businessPartner } from '../../../../models/entities/businessPartner/businessPartner';
import Overview from './sections/Overview';
import { styled } from '../../../shared/Theme/theme';
import Button from '../../../shared/SmallComponents/Button';
import EditIcon from '../../../../static/icons/edit.svg';
import BackButton from '../../../shared/SmallComponents/BackButton';
import CompanyTabs from './sections/Tabs';
import CompactHeader from './sections/CompactHeader';
import CustomModal, { ModalTriggerProps } from '../../../shared/Modal/Modal';
import BusinessPartnerFormConnected, { formMode } from '../components/BusinessPartnerForm/BusinessPartnerFormConnected';
import SendGetStartedInventationModal from '../components/SendGetStartedInventation/SendGetStartedInventationModal';
import { settings } from '../../../../models/entities/businessPartner/businessPartner';
import DiscussionsSection from './sections/Discussions/DiscussionsSection';
import { allowedCompany, userInfo } from '../../../../models/entities/user';
import { product } from '../../../../models/entities/product/product';
import { drawers } from '../../../../state/ducks/header/header';
import { RcFile } from 'antd/lib/upload';

/** Matches row actions on the business partners grid: edit when the BP is in the user's allowed companies. */
const canEditBusinessPartnerInAllowedCompanies = (businessPartner: businessPartner, allowedCompanies: allowedCompany[]): boolean =>
    allowedCompanies.map((item) => item.companyId).includes(businessPartner.companyId);

type Props = {
    businessPartner: businessPartner;
    localization?: any;
    settings?: settings;
    allowedCompanies: allowedCompany[];
    products: product[];
    setDrawerOpen: (open: drawers, params?: any) => void;
    updateBusinessPartnerIsActive: (businessPartnerId: string, isActive: boolean) => Promise<void>;
    updateBusinessPartnerLogo: (businessPartnerId: string, file: RcFile) => Promise<void>;
    showLogoLoader: boolean;
    loadingIsActive: boolean;
    isForwarder: boolean;
    users: userInfo[];
};

const SingleBusinessPartnerPage = ({
    businessPartner,
    products,
    localization,
    settings,
    allowedCompanies,
    updateBusinessPartnerLogo,
    showLogoLoader,
    setDrawerOpen,
    updateBusinessPartnerIsActive,
    loadingIsActive,
    isForwarder,
    users
}: Props) => {
    const [editModalVisible, setEditModalVisible] = useState(false);

    const canEdit = canEditBusinessPartnerInAllowedCompanies(businessPartner, allowedCompanies);

    const headerActions = (
        <>
            {isForwarder && (
                <SendGetStartedInventationModal
                    businessPartner={businessPartner}
                    Trigger={(props: ModalTriggerProps) => (
                        <Button onClick={props.onClick} buttonType="filled" margin="0 4px 0 0">
                            {localization.invite_to_join_cargozone}
                        </Button>
                    )}
                />
            )}
            <Switch
                loading={loadingIsActive}
                checked={businessPartner.isActive}
                onChange={async (checked: boolean) => await updateBusinessPartnerIsActive(businessPartner.id, checked)}
                checkedChildren={localization.active}
                unCheckedChildren={localization.inactive}
            />
            {canEdit && (
                <CustomModal
                    isOpen={editModalVisible}
                    title={{ text: localization.modal.edit_title }}
                    Trigger={(props: ModalTriggerProps) => (
                        <Button
                            onClick={() => {
                                setEditModalVisible(true);
                                props.onClick();
                            }}
                            margin="0 0 0 4px"
                            buttonType="hollow"
                        >
                            <img src={EditIcon} alt={localization.edit} />
                            <span>{localization.edit}</span>
                        </Button>
                    )}
                    Content={(
                        setVisible?: React.Dispatch<React.SetStateAction<boolean>>,
                        setConfirm?: React.Dispatch<React.SetStateAction<boolean>>
                    ) => {
                        return (
                            <BusinessPartnerFormConnected
                                mode={formMode.EDIT}
                                party={businessPartner}
                                openedFromModal={setVisible ? { setVisible } : undefined}
                                confirmation={setConfirm ? { setConfirm } : undefined}
                            />
                        );
                    }}
                    width="393px"
                    onCancelCallback={() => setEditModalVisible(false)}
                />
            )}
        </>
    );

    const isCargoOwner = businessPartner.type === 'CARGO_OWNER';

    return (
        <PageWrapper $tinted={isCargoOwner}>
            <LeftSide>
                <BackRow>
                    <BackButton style={{ marginLeft: '-30px' }} toPath={PagesRoutes.BusinessPartners} text={localization.backLink} />
                    {businessPartner.deletedAt && <InactiveBadge>{localization.deleted}</InactiveBadge>}
                </BackRow>
                <CompactHeader businessPartner={businessPartner} settings={settings} users={users} actions={headerActions} />
                {!isCargoOwner && (
                    <OverviewSectionWrapper>
                        <Overview
                            updateBusinessPartnerLogo={updateBusinessPartnerLogo}
                            showLogoLoader={showLogoLoader}
                            businessPartner={businessPartner}
                            localization={localization}
                            settings={settings}
                            users={users}
                        />
                    </OverviewSectionWrapper>
                )}
                <TabsCard $tinted={isCargoOwner}>
                    <CompanyTabs
                        businessPartner={businessPartner}
                        products={products}
                        localization={localization}
                        settings={settings}
                        setDrawerOpen={setDrawerOpen}
                        users={users}
                        updateBusinessPartnerLogo={updateBusinessPartnerLogo}
                        showLogoLoader={showLogoLoader}
                    />
                </TabsCard>
                {!isCargoOwner && (
                    <SectionWrapper>
                        <DiscussionsSection localization={localization} />
                    </SectionWrapper>
                )}
            </LeftSide>
        </PageWrapper>
    );
};

const PageWrapper = styled.div<{ $tinted?: boolean }>`
    display: flex;
    flex-direction: row;
    background: ${(p) => (p.$tinted ? '#f4f6fb' : 'transparent')};
    min-height: ${(p) => (p.$tinted ? 'calc(100vh - 65px)' : 'auto')};

    @media screen and (max-width: 768px) {
        height: calc(100vh - 65px - 40px);
    }
`;

const LeftSide = styled.div`
    width: 100%;
    border-right: 1px solid ${(props) => props.theme.colors.grayDefaultBorder};

    @media screen and (max-width: 1200px) {
        width: 100%;
        max-width: 100%;
    }
`;

const OverviewSectionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: solid 1px ${(props) => props.theme.colors.grayDefaultBorder};
    justify-content: space-between;
`;

export const SectionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    padding: 24px 18px 18px 48px;
    border-bottom: solid 1px ${(props) => props.theme.colors.grayDefaultBorder};
    justify-content: space-between;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 18px 4px 48px;
    & a, & a:visited {
        color: #1f7ab8;
    }
`;

const TabsCard = styled.div<{ $tinted?: boolean }>`
    display: flex;
    flex-direction: column;
    background: ${(p) => (p.$tinted ? '#fff' : 'transparent')};
    border: ${(p) => (p.$tinted ? '1px solid #e5e7eb' : 'none')};
    border-radius: ${(p) => (p.$tinted ? '12px' : '0')};
    box-shadow: ${(p) => (p.$tinted ? '0 1px 2px rgba(20, 30, 60, 0.04)' : 'none')};
    margin: ${(p) => (p.$tinted ? '0 18px 18px' : '0')};
    padding: ${(p) => (p.$tinted ? '6px 16px 16px' : '4px 18px 18px 18px')};
    overflow: hidden;

    /* Override the shared Tab styling for the BP page only */
    ${(p) =>
        p.$tinted &&
        `
        & #tabs-container > div:first-child {
            border-bottom: 1px solid #eef0f3;
        }
        & #container {
            justify-content: flex-start;
            min-width: 0 !important;
        }
        & #container > div {
            flex: 0 0 auto;
            font-size: 14px;
            font-weight: 500;
            padding: 12px 18px 10px;
            height: auto;
            border-bottom-width: 2px !important;
            min-width: auto;
            color: #6b7280;
        }
        & #container > div:hover {
            color: #1f7ab8;
        }
    `}
`;

const InactiveBadge = styled.span`
    border-radius: 2vh;
    border: 1px solid #ff7676;
    color: #ff7676;
    font-size: 14px;
    height: 24px;
    padding: 3px 8px;
    margin-left: 10px;
    display: inline-flex;
    align-items: center;
    line-height: 1px;
    align-self: flex-end;
    min-width: max-content;
`;

export default SingleBusinessPartnerPage;
